import { useEffect, useState } from 'react';
import useAuth from 'src/hooks/useAuth';
import { marketplace } from 'src/backend';

// 24h TTL: seller/listing density changes slowly, and this keeps the probes
// to ~2 lightweight queries per team per day shared by every consumer
// (home promo card + sidebar menu item).
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

const cacheKey = (teamId) => `marketplace_relevance_${teamId}`;

const readCache = (teamId) => {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(cacheKey(teamId)));
    return typeof parsed?.show === 'boolean' ? parsed : null;
  } catch {
    return null;
  }
};

const writeCache = (teamId, show) => {
  try {
    window.localStorage.setItem(
      cacheKey(teamId),
      JSON.stringify({ show, time: Date.now() })
    );
  } catch {
    // localStorage unavailable — we'll just re-probe next mount
  }
};

/**
 * Whether marketplace entry points (home promo card, sidebar menu item) are
 * relevant for the current nonprofit: active listings within 100 miles OR
 * existing buyer order history. Always true for non-nonprofit accounts.
 *
 * A stale cached value is served while revalidating so entry points don't
 * flash in/out for established users.
 */
export default function useNonprofitMarketplaceRelevance() {
  const { user } = useAuth();
  const teamId = user?.team_id;
  // Drivers never see the gated entry points, and /marketplace/orders/me
  // 403s for them (requireMarketplaceManager), so don't probe.
  const enabled =
    !!user?.is_non_profit &&
    !user?.marketplace_hidden &&
    !user?.is_driver &&
    !!teamId;

  const [show, setShow] = useState(() =>
    enabled ? (readCache(teamId)?.show ?? false) : true
  );

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    const cached = readCache(teamId);
    if (cached) {
      setShow(cached.show);
      if (Date.now() - cached.time < CACHE_TTL_MS) {
        return undefined;
      }
    }

    let cancelled = false;
    Promise.allSettled([
      marketplace.browseListings({ step: 1, offset: 0, distance: 100 }),
      marketplace.getMyOrders({ step: 1, offset: 0 }),
    ]).then(([listings, orders]) => {
      const results = [listings, orders];
      const anyPositive = results.some(
        (result) =>
          result.status === 'fulfilled' && (result.value?.count ?? 0) > 0
      );
      const rejected = results.find((result) => result.status === 'rejected');
      if (!anyPositive && rejected) {
        // A fulfilled zero can't rule out relevance while the other probe is
        // unknown — keep the cached/initial value and don't poison the cache.
        console.error(rejected.reason);
        return;
      }
      const next = anyPositive;
      writeCache(teamId, next);
      if (!cancelled) {
        setShow(next);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, teamId]);

  return { show };
}
